import { useCallback, useEffect, useRef, useState } from "react";
import { formatInrCompact, formatInrFull } from "@/lib/money";

/**
 * RU-UIMOBILE UM-2: a money value shown compact (₹6.05L) with the exact value in title / aria-label.
 * Keyboard focus shows the exact value; a tap or click toggles compact ⇄ exact; blur returns to compact.
 *
 * RU-UIFU UF-4: an accessible toggle — role="button", named by the exact value, aria-pressed = exact shown,
 * Enter / Space toggle. UF-5: `interactive={false}` (dense tables) is a plain, non-focusable span: the compact text
 * is aria-hidden and the exact value is visually hidden text (and the title), so a long table adds no tab stops.
 *
 * RU-TAPFIX: an exact value that wraps makes its grid row taller. Collapsing on the blur caused by a press elsewhere
 * shrank the row between press and release, so the release (and its click) missed the tile that was tapped. A blur
 * that follows a pointer press outside therefore collapses only once that interaction has landed: on its `click`
 * (document capture — the click's target is already fixed), on `pointercancel`, on any key, or after a short
 * fallback timeout. Keyboard blur and Escape collapse at once. Document listeners exist only while expanded / pending.
 */
export const COLLAPSE_FALLBACK_MS = 350;

export default function Money({ value, plus = false, decimals = 2, className = "", testid, interactive = true }) {
  const [exact, setExact] = useState(false);
  const pointer = useRef(false);          // a press on this value is in progress (its focus must not expand it)
  const elRef = useRef(null);
  const pressedOutside = useRef(false);   // a pointer press outside happened while expanded (and no key since)
  const pointerDown = useRef(false);      // ... and has not been released yet
  const pending = useRef(null);           // cleanup of a pending (deferred) collapse

  const cancelPending = useCallback(() => {
    if (pending.current) { pending.current(); pending.current = null; }
  }, []);
  const collapseNow = useCallback(() => { cancelPending(); setExact(false); }, [cancelPending]);

  // While expanded: note pointer presses outside (they will blur this value) and keys (a key means a keyboard blur).
  useEffect(() => {
    if (!exact) return undefined;
    const onDown = (e) => {
      if (elRef.current && elRef.current.contains(e.target)) return;
      pressedOutside.current = true;
      pointerDown.current = true;
    };
    const onUp = () => { pointerDown.current = false; };
    const onKey = () => { pressedOutside.current = false; };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("pointerup", onUp, true);
    document.addEventListener("pointercancel", onUp, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("pointerup", onUp, true);
      document.removeEventListener("pointercancel", onUp, true);
      document.removeEventListener("keydown", onKey, true);
      pressedOutside.current = false;
      pointerDown.current = false;
    };
  }, [exact]);

  // Unmount: drop a pending collapse (its listeners and timer).
  useEffect(() => () => cancelPending(), [cancelPending]);

  // Collapse once the pointer interaction that caused the blur has landed.
  const deferCollapse = useCallback(() => {
    cancelPending();
    let timer = null;
    function cleanup() {
      clearTimeout(timer);
      document.removeEventListener("click", finish, true);
      document.removeEventListener("pointercancel", finish, true);
      document.removeEventListener("keydown", finish, true);
      document.removeEventListener("pointerup", arm, true);
    }
    function finish() { cleanup(); pending.current = null; setExact(false); }
    function arm() { clearTimeout(timer); timer = setTimeout(finish, COLLAPSE_FALLBACK_MS); }
    document.addEventListener("click", finish, true);
    document.addEventListener("pointercancel", finish, true);
    document.addEventListener("keydown", finish, true);
    if (pointerDown.current) document.addEventListener("pointerup", arm, true);  // mouse: blur on the press
    else arm();                                                                 // touch: blur after the release
    pending.current = cleanup;
  }, [cancelPending]);

  const compactText = formatInrCompact(value, { plus, decimals });
  const fullText = formatInrFull(value, { plus, decimals: 2 });
  if (compactText === "—") {
    return <span data-testid={testid} className={className}>—</span>;
  }
  if (!interactive) {
    return (
      // `relative`: the sr-only text is absolutely positioned; anchored here it stays inside the table's own scroller
      // instead of widening the page (found by the UM-11 check: document 567 px at 390 px).
      <span title={fullText} className={`relative [overflow-wrap:anywhere] ${className}`}>
        <span data-testid={testid} aria-hidden="true">{compactText}</span>
        <span className="sr-only">{fullText}</span>
      </span>
    );
  }
  return (
    <span
      ref={elRef}
      data-testid={testid}
      data-exact={exact ? "true" : "false"}
      role="button"
      aria-pressed={exact ? "true" : "false"}
      title={fullText}
      aria-label={fullText}
      tabIndex={0}
      onPointerDown={() => { pointer.current = true; }}
      onFocus={() => {
        cancelPending();               // re-focused before a pending collapse: it stays as it is
        if (!pointer.current) setExact(true);
      }}
      onClick={() => { pointer.current = false; cancelPending(); setExact((v) => !v); }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
          e.preventDefault();          // Space would scroll the page
          setExact((v) => !v);
        } else if (e.key === "Escape" && exact) {
          collapseNow();
        }
      }}
      onBlur={() => {
        pointer.current = false;
        if (exact && pressedOutside.current) deferCollapse();   // RU-TAPFIX TF-1
        else collapseNow();                                     // TF-3: keyboard / programmatic blur
      }}
      className={`cursor-pointer [overflow-wrap:anywhere] ${className}`}
    >
      {exact ? fullText : compactText}
    </span>
  );
}
